/**
 * Geofences Management Page
 * Create, edit, and manage geofences with map-based drawing
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  TextInput,
  Platform,
  useWindowDimensions,
  Dimensions,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Surface, useTheme, Appbar } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import gpsTrackingAPI from '../../../src/api/services/gpsTracking';
import GeofenceForm from '../../../src/components/GPS/GeofenceForm';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import type { Geofence } from '../../../src/types/gps';
import Logo from '../../../src/components/Logo';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function GeofencesPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const isWeb = Platform.OS === 'web';
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  
  const { handleLogout, userData } = useUser();
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  
  useEffect(() => {
    if (isLargeScreen) {
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      setDesktopSidebarVisible(false);
    }
  }, [isLargeScreen, width]);

  useEffect(() => {
    loadGeofences();
  }, []);

  const loadGeofences = async () => {
    try {
      setLoading(true);
      const data = await gpsTrackingAPI.geofences.listGeofences();
      setGeofences(data);
    } catch (error) {
      console.error('Failed to load geofences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGeofence = async (data: any) => {
    // Close modal and reload list
    setShowCreateModal(false);
    setEditingGeofence(null);
    await loadGeofences();
  };

  const handleOpenEditor = (geofence?: Geofence) => {
    if (!isWeb) {
      Alert.alert(
        'Disponível apenas na web',
        'A criação e edição de geofences está disponível somente no navegador. Abra a versão web para desenhar áreas no mapa.'
      );
      return;
    }

    setEditingGeofence(geofence || null);
    setShowCreateModal(true);
  };

  const handleEditGeofence = (geofence: Geofence) => {
    handleOpenEditor(geofence);
  };

  const handleDeleteGeofence = (geofence: Geofence) => {
    Alert.alert(
      'Confirmar exclusão',
      `Tem certeza que deseja excluir a geofence "${geofence.name}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Excluir',
          style: 'destructive',
          onPress: async () => {
            try {
              await gpsTrackingAPI.geofences.deleteGeofence(geofence.id);
              await loadGeofences();
              Alert.alert('Sucesso', 'Geofence excluída com sucesso');
            } catch (error) {
              Alert.alert('Erro', 'Falha ao excluir geofence');
            }
          },
        },
      ]
    );
  };

  const handleToggleActive = async (geofence: Geofence) => {
    try {
      await gpsTrackingAPI.geofences.updateGeofence(geofence.id, {
        is_active: !geofence.is_active,
      });
      await loadGeofences();
    } catch (error) {
      Alert.alert('Erro', 'Falha ao atualizar geofence');
    }
  };

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.outerContainer}>
      {/* Desktop Sidebar */}
      {desktopSidebarVisible && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Main Content */}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[styles.container, !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }]}>
          {/* Loading state */}
          {loading && (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="large" color="#007AFF" />
            </View>
          )}

          {/* Main content */}
          {!loading && (
            <>
      {/* Header with Create Button */}
      <View style={styles.pageHeader}>
        <Text style={styles.pageTitle}>Geofences ({geofences.length})</Text>
        <TouchableOpacity
          style={styles.createButtonHeader}
          onPress={() => handleOpenEditor()}
        >
          <Ionicons name="add-circle" size={20} color="#FFFFFF" />
          <Text style={styles.createButtonHeaderText}>Nova Geofence</Text>
        </TouchableOpacity>
      </View>

      {!isWeb && (
        <View style={styles.mobileWarning}>
          <Ionicons name="alert-circle-outline" size={20} color="#FF3B30" />
          <Text style={styles.mobileWarningText}>
            A criação e edição de geofences está disponível apenas na versão web. Abra no navegador para desenhar áreas no mapa.
          </Text>
        </View>
      )}

      {/* Geofence List */}
      <ScrollView style={styles.list} contentContainerStyle={geofences.length === 0 ? styles.listContentContainer : undefined}>

        {geofences.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="shapes-outline" size={64} color="#C7C7CC" />
            <Text style={styles.emptyStateTitle}>Nenhuma Geofence</Text>
            <Text style={styles.emptyStateText}>
              Crie sua primeira geofence para monitorar entradas e saídas de áreas específicas
            </Text>
            <TouchableOpacity
              style={styles.createButtonEmpty}
              onPress={() => handleOpenEditor()}
            >
              <Ionicons name="add-circle-outline" size={20} color="#FFFFFF" />
              <Text style={styles.createButtonEmptyText}>Criar Geofence</Text>
            </TouchableOpacity>
          </View>
        ) : (
          geofences.map((geofence) => (
            <View key={geofence.id} style={styles.geofenceCard}>
              <View style={styles.geofenceHeader}>
                <View style={styles.geofenceTitleRow}>
                  <View
                    style={[
                      styles.colorIndicator,
                      { backgroundColor: geofence.color },
                    ]}
                  />
                  <Text style={styles.geofenceName}>{geofence.name}</Text>
                  {geofence.is_preset && (
                    <View style={styles.presetBadge}>
                      <Text style={styles.presetBadgeText}>Preset</Text>
                    </View>
                  )}
                  {!geofence.is_active && (
                    <View style={styles.inactiveBadge}>
                      <Text style={styles.inactiveBadgeText}>Inativa</Text>
                    </View>
                  )}
                </View>

                <View style={styles.geofenceActions}>
                  <TouchableOpacity
                    onPress={() => handleToggleActive(geofence)}
                    style={styles.actionButton}
                  >
                    <Ionicons
                      name={geofence.is_active ? 'pause-circle-outline' : 'play-circle-outline'}
                      size={20}
                      color={geofence.is_active ? '#FF9500' : '#34C759'}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleEditGeofence(geofence)}
                    style={styles.actionButton}
                  >
                    <Ionicons name="create-outline" size={20} color="#007AFF" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleDeleteGeofence(geofence)}
                    style={styles.actionButton}
                  >
                    <Ionicons name="trash-outline" size={20} color="#FF3B30" />
                  </TouchableOpacity>
                </View>
              </View>

              {geofence.description && (
                <Text style={styles.geofenceDescription}>
                  {geofence.description}
                </Text>
              )}

              <View style={styles.geofenceInfo}>
                <View style={styles.geofenceInfoItem}>
                  <Ionicons name="shapes-outline" size={14} color="#8E8E93" />
                  <Text style={styles.geofenceInfoText}>
                    {geofence.geometry_type}
                  </Text>
                </View>
                <View style={styles.geofenceInfoItem}>
                  <Ionicons name="time-outline" size={14} color="#8E8E93" />
                  <Text style={styles.geofenceInfoText}>
                    {new Date(geofence.created_at).toLocaleDateString()}
                  </Text>
                </View>
              </View>
            </View>
          ))
        )}
      </ScrollView>
            </>
          )}
        </View>
      </Surface>

      {/* Create/Edit Modal */}
      <Modal
        visible={showCreateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[
            styles.modalContent,
            { width: Platform.OS === 'web' && width > 768 ? 600 : '90%' }
          ]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingGeofence ? 'Editar Geofence' : 'Nova Geofence'}
              </Text>
              <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                <Ionicons name="close" size={24} color="#000" />
              </TouchableOpacity>
            </View>
            
            <GeofenceForm
              initialData={editingGeofence || undefined}
              onSubmit={handleCreateGeofence}
              onCancel={() => setShowCreateModal(false)}
              isEditing={!!editingGeofence}
            />
          </View>
        </View>
      </Modal>

      {/* Mobile Sidebar */}
      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  menuButton: {
    padding: 4,
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  createForm: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  formRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  input: {
    flex: 1,
    height: 44,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 15,
    color: '#000000',
    backgroundColor: '#FFFFFF',
  },
  closeButton: {
    padding: 10,
    justifyContent: 'center',
  },
  formHint: {
    fontSize: 13,
    color: '#8E8E93',
    fontStyle: 'italic',
    marginTop: 4,
  },
  mapContainer: {
    height: 300,
    backgroundColor: '#E5E5EA',
  },
  map: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  list: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  listContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  geofenceCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginBottom: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  geofenceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  geofenceTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  colorIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  geofenceName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    flex: 1,
  },
  presetBadge: {
    backgroundColor: '#E5F1FF',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  presetBadgeText: {
    fontSize: 12,
    color: '#007AFF',
    fontWeight: '600',
  },
  inactiveBadge: {
    backgroundColor: '#E5E5EA',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  inactiveBadgeText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  geofenceActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    padding: 4,
  },
  geofenceDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 8,
  },
  geofenceInfo: {
    flexDirection: 'row',
    gap: 16,
  },
  geofenceInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  geofenceInfoText: {
    fontSize: 14,
    color: '#8E8E93',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
    maxWidth: 500,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#C7C7CC',
    marginTop: 4,
    textAlign: 'center',
  },
  comingSoonBadgeLarge: {
    backgroundColor: '#8E8E93',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 6,
    alignSelf: 'center',
  },
  comingSoonTextLarge: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
  },
  mobileWarning: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: '#FFF5F5',
    padding: 12,
    marginHorizontal: 16,
    marginTop: 12,
    borderRadius: 8,
  },
  mobileWarningText: {
    flex: 1,
    fontSize: 13,
    color: '#FF3B30',
    lineHeight: 18,
  },
  createButtonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#007AFF',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  createButtonHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  createButtonEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  createButtonEmptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    maxHeight: '90%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000000',
  },
});

